import { useMemo } from "react";

import {
  SurveyViewResponse,
  SurveyViewPublicationDestination,
  SurveyViewWithSurvey,
} from "@/redux/survey-views/types";
import { OrganizationRoles } from "@/shared/utils/constants";
import { SurveyViewPermissions } from "@/domains/survey-views/types";
import { FullUserResponse } from "@/redux/users/types";

export const useSurveyViewPermissions = (
  surveyView: SurveyViewResponse | SurveyViewWithSurvey | null | undefined,
  currentUser: FullUserResponse | null | undefined,
): SurveyViewPermissions => {
  return useMemo(() => {
    if (!currentUser) {
      return {
        canEdit: false,
        canDelete: false,
        canCreate: false,
      };
    }

    const isAdmin =
      currentUser.organizationRole === OrganizationRoles.ADMIN.value;
    const canCreate =
      currentUser.organizationRole &&
      [
        OrganizationRoles.ADMIN.value,
        OrganizationRoles.SUPERVISOR.value,
      ].includes(currentUser.organizationRole);

    const isOwner = surveyView
      ? currentUser.id === surveyView.createdBy?.id
      : false;
    const isSameOrganization = surveyView
      ? currentUser.organization?.id === surveyView.organization.id
      : false;
    const isWebsitePublication = surveyView
      ? surveyView.config.publicationDestination ===
        SurveyViewPublicationDestination.WEBSITE
      : false;
    const isInAppPublication = surveyView
      ? surveyView.config.publicationDestination ===
        SurveyViewPublicationDestination.IN_APP
      : false;

    const canEdit = Boolean(
      surveyView && isSameOrganization && (isOwner || isAdmin),
    );
    const canDelete = Boolean(
      surveyView &&
        !isWebsitePublication &&
        !isInAppPublication &&
        isSameOrganization &&
        (isOwner || isAdmin),
    );

    return {
      canEdit,
      canDelete,
      canCreate,
    };
  }, [surveyView, currentUser]);
};
